import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import UserProfile from '../../auth/UserProfile';
import { routes } from '../../routes/router';
import { useUserGroups } from '../../hooks/useUserGroups';
import ReportSearch from '../../components/ReportSearch';

const DashboardLayout: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { groups, loading: groupsLoading } = useUserGroups();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleNavClick = (route: string, e: React.MouseEvent) => {
    e.preventDefault();
    navigate({ pathname: route, search: '' });
  };

  return (
    <div className="dashboard-root">
      <header className="dashboard-header">
        {isMobile ? (
          <div className="dashboard-header-mobile-row">
            <div className="dashboard-logo">
              <Link to="/">
                <img src="/icon-192x192.png" alt="Hilco Vision Icon" className="dashboard-logo-mobile" style={{ height: 40, width: 40, background: 'transparent' }} />
              </Link>
            </div>
            <nav className="dashboard-nav">
              {groupsLoading ? (
                <span>{t('common.loading')}</span>
              ) : (
                routes.filter(r => r.label && r.element).map(route => {
                  const navPath = route.path.replace(/\/*$/, '');
                  const isActive = new RegExp(`^${navPath}(\/|$)`).test(location.pathname);
                  const requiredGroups = route.requiredGroups;
                  if (requiredGroups && requiredGroups.length > 0) {
                    const normalize = (s: string) => s.trim().toLowerCase();
                    const normalizedUserGroups = groups.map(normalize);
                    const normalizedRequiredGroups = requiredGroups.map(normalize);
                    const isInGroup = normalizedUserGroups.some(userGroup => 
                      normalizedRequiredGroups.includes(userGroup)
                    );
                    if (!isInGroup) return null;
                  }
                  const labelKey = route.label === 'Reports' ? 'navigation.reports' : route.label === 'Management' ? 'navigation.management' : route.label || '';
                  return (
                    <Link
                      key={navPath}
                      to={navPath}
                      className={isActive ? 'active' : ''}
                      onClick={e => handleNavClick(navPath, e)}
                    >
                      {labelKey ? t(labelKey) : route.label}
                    </Link>
                  );
                })
              )}
            </nav>
            <div className="dashboard-user-group">
              <div className="dashboard-user">
                <UserProfile />
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="dashboard-logo">
              <Link to="/">
                <img src="/hilco-vision-full-logo.png" alt="Hilco Vision Logo" style={{ height: 40 }} />
              </Link>
            </div>
            <nav className="dashboard-nav">
              {groupsLoading ? (
                <span>{t('common.loading')}</span>
              ) : (
                routes.filter(r => r.label && r.element).map(route => {
                  const navPath = route.path.replace(/\/*$/, '');
                  const isActive = new RegExp(`^${navPath}(\/|$)`).test(location.pathname);
                  const requiredGroups = route.requiredGroups;
                  if (requiredGroups && requiredGroups.length > 0) {
                    const normalize = (s: string) => s.trim().toLowerCase();
                    const normalizedUserGroups = groups.map(normalize);
                    const normalizedRequiredGroups = requiredGroups.map(normalize);
                    const isInGroup = normalizedUserGroups.some(userGroup => 
                      normalizedRequiredGroups.includes(userGroup)
                    );
                    if (!isInGroup) return null;
                  }
                  const labelKey = route.label === 'Reports' ? 'navigation.reports' : route.label === 'Management' ? 'navigation.management' : route.label || '';
                  return (
                    <Link
                      key={navPath}
                      to={navPath}
                      className={isActive ? 'active' : ''}
                      onClick={e => handleNavClick(navPath, e)}
                    >
                      {labelKey ? t(labelKey) : route.label}
                    </Link>
                  );
                })
              )}
            </nav>
            <div className="dashboard-user" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {!isMobile && <ReportSearch />}
              <UserProfile />
            </div>
          </>
        )}
      </header>
      {children}
    </div>
  );
};

export default DashboardLayout; 